/**
 * Promociones del carrito (snippets/multiple-promo-product-cart.liquid).
 *
 * Las reglas viven solo en Liquid: cada render trae el estado de las promos y un plan de
 * correcciones (quitar líneas, dejarlas en 1, auto-añadir). Este archivo solo ejecuta planes:
 *   - antes de mutar, vuelve a pedir la sección para usar el plan fresco del servidor,
 *   - aplica el plan en 1–2 peticiones y re-renderiza el carrito con la misma respuesta,
 *   - atiende "Agregar" / "Quitar" y la validación antes de pagar.
 *
 * cart.js y cart-drawer.js reemplazan el HTML de main-cart-items en cada cambio del carrito, así
 * que <multiple-promo-cart> se vuelve a conectar con un plan fresco (sincronización lista →
 * módulo) sin MutationObserver ni scripts inline.
 */
(() => {
  if (customElements.get('multiple-promo-cart')) return;

  const SLOT_PROPERTY = '_promo_slot';
  const AUTO_ATTRIBUTE = '_promo_auto';
  const MAX_REPEATED_PLANS = 3;
  const FLASH_TTL = 10000;
  const APP_SESSION_TIMEOUT = 5000;
  const GENERIC_ERROR = 'No pudimos actualizar tu carrito. Inténtalo nuevamente.';

  const route = (name, fallback) => (window.routes && window.routes[name]) || fallback;

  // snippets/loccitane-app-session.liquid: mientras sincroniza el atributo de la app, el HTML puede
  // traer una elegibilidad Provenzal vieja. Se espera, con un tope para no bloquear el carrito.
  let appSession = null;
  function waitForAppSession() {
    if (!appSession) {
      const ready = window.LoccitaneApp && window.LoccitaneApp.ready;
      appSession = ready
        ? Promise.race([ready.catch(() => {}), new Promise((resolve) => setTimeout(resolve, APP_SESSION_TIMEOUT))])
        : Promise.resolve();
    }
    return appSession;
  }

  function withoutProducts(attribute, productIds) {
    return productIds.reduce((value, id) => value.replace(`,${id},`, ','), attribute);
  }

  /**
   * Plan de Liquid sin los auto-añadidos excluidos (fallaron antes, o no se quieren ahora). Esos
   * productos también salen de `_promo_auto`: si no se agregaron, no deben parecer rechazados.
   */
  function resolvePlan(plan, excludedProductIds) {
    const excluded = plan.adds.filter((add) => excludedProductIds.has(add.productId)).map((add) => add.productId);
    const autoAttribute = withoutProducts(plan.autoAttribute, excluded);
    return {
      ...plan,
      adds: plan.adds.filter((add) => !excludedProductIds.has(add.productId)),
      autoAttribute,
      attributeChanged: autoAttribute !== plan.autoAttributeCurrent,
    };
  }

  function hasCorrections(plan) {
    return Object.keys(plan.updates).length > 0;
  }

  function hasWork(plan) {
    return hasCorrections(plan) || plan.adds.length > 0 || plan.attributeChanged;
  }

  // Por qué no se puede agregar una promo, según su estado en Liquid (state) y si cumple (status "y").
  function blockedReason(slot) {
    switch (slot.state) {
      case 'locked':
        return 'Esta promoción es exclusiva para clientes del Club Provenzal.';
      case 'blocked':
        return 'Solo puedes llevar 1 producto de esta promoción. Quita el elegido para cambiarlo.';
      case 'chosen':
        return 'Ya tienes este producto en tu carrito.';
      default:
        return slot.status === 'y'
          ? 'Este producto ya no tiene stock disponible.'
          : 'Todavía no cumples la condición de esta promoción.';
    }
  }

  // ---------------------------------------------------------------------------
  // Red y secciones
  // ---------------------------------------------------------------------------

  async function requestJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const error = new Error((data && (data.description || data.message)) || `HTTP ${response.status}`);
      error.userMessage = data && typeof data.description === 'string' ? data.description : null;
      throw error;
    }
    return data;
  }

  // Mismas secciones y selectores que CartItems.getSectionsToRender (assets/cart.js).
  function getSectionTargets() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'sticky-checkout',
        section: document.getElementById('sticky-checkout')?.dataset.id,
        selector: '.sticky-checkout-container',
      },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ].filter((target) => target.section && document.getElementById(target.id));
  }

  function renderSections(sections, targets) {
    if (!sections) return;

    for (const target of targets) {
      const html = sections[target.section];
      const host = document.getElementById(target.id);
      if (!html || !host) continue;

      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
      if (source) (host.querySelector(target.selector) || host).innerHTML = source.innerHTML;
    }
  }

  function readConfig(root) {
    const script = root.querySelector('script[data-mpc-config]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('[Promos carrito] Configuración inválida', error);
      return null;
    }
  }

  /** Vuelve a renderizar el carrito en el servidor y devuelve el plan fresco (sin tocar el DOM). */
  async function fetchFresh() {
    const targets = getSectionTargets();
    const main = targets.find((target) => target.id === 'main-cart-items');
    if (!main) return { config: null, sections: null, targets };

    const response = await fetch(`${route('cart_url', '/cart')}?sections=${main.section}`, { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const sections = await response.json();
    const doc = new DOMParser().parseFromString(sections[main.section] || '', 'text/html');
    return { config: readConfig(doc), sections, targets };
  }

  // ---------------------------------------------------------------------------
  // Controlador (una operación a la vez)
  // ---------------------------------------------------------------------------

  class PromoController {
    constructor() {
      this.queue = Promise.resolve();
      this.syncQueued = false;
      this.busyCount = 0;
      this.flash = null;
      this.lastPlanKey = null;
      this.repeatedPlans = 0;
      // Auto-añadidos que fallaron (p. ej. sin stock): no se reintentan durante esta visita.
      this.failedAutoAdds = new Set();
    }

    get element() {
      return document.querySelector('multiple-promo-cart');
    }

    enqueue(task) {
      const run = this.queue.then(waitForAppSession).then(task);
      this.queue = run.catch(() => {});
      return run;
    }

    requestSync() {
      if (this.syncQueued) return;
      this.syncQueued = true;

      this.enqueue(() => {
        this.syncQueued = false;
        return this.sync();
      }).catch((error) => console.error('[Promos carrito] sync', error));
    }

    /** Plan de Liquid listo para ejecutar; `includeAdds: false` descarta los auto-añadidos. */
    resolve(plan, { includeAdds = true } = {}) {
      const excluded = includeAdds ? this.failedAutoAdds : new Set(plan.adds.map((add) => add.productId));
      return resolvePlan(plan, excluded);
    }

    /** Aplica el plan del servidor. En estado estable (plan vacío en el HTML) no hace requests. */
    async sync() {
      const config = this.element?.config;
      if (!config || config.designMode || !hasWork(this.resolve(config.plan))) {
        this.resetLoopGuard();
        return;
      }

      const fresh = await fetchFresh();
      const plan = fresh.config && this.resolve(fresh.config.plan);
      if (!plan || !hasWork(plan)) {
        // El HTML estaba desactualizado: se muestra el estado real.
        this.resetLoopGuard();
        renderSections(fresh.sections, fresh.targets);
        return;
      }
      if (this.isLooping(plan)) return;

      await this.applyPlan(plan, fresh.targets);
    }

    /**
     * Ejecuta un plan resuelto: primero los auto-añadidos (add.js), después correcciones +
     * atributo `_promo_auto` + re-render en un solo update.js.
     */
    async applyPlan(plan, targets) {
      this.setBusy(true);

      try {
        const notices = [...plan.notices];
        const added = [];
        let attribute = plan.autoAttribute;

        for (const add of plan.adds) {
          try {
            await requestJSON(`${route('cart_add_url', '/cart/add')}.js`, {
              items: [{ id: add.variantId, quantity: 1, properties: { [SLOT_PROPERTY]: String(add.slot) } }],
            });
            added.push(add.title);
          } catch (error) {
            // Si no se pudo agregar, no se reintenta ni queda marcado como "auto-añadido" (parecería rechazado).
            this.failedAutoAdds.add(add.productId);
            attribute = withoutProducts(attribute, [add.productId]);
            notices.push(error.userMessage || `No pudimos agregar ${add.title}.`);
          }
        }
        if (added.length > 0) notices.push(`Agregamos a tu carrito: ${added.join(', ')}.`);

        const body = { sections: targets.map((target) => target.section), sections_url: window.location.pathname };
        if (hasCorrections(plan)) body.updates = plan.updates;
        if (attribute !== plan.autoAttributeCurrent) body.attributes = { [AUTO_ATTRIBUTE]: attribute };

        if (!body.updates && !body.attributes) {
          // Nada que actualizar (todos los auto-añadidos fallaron): solo se muestra el estado real.
          this.setFlash(notices.join(' '), 'error');
          await this.refreshSections();
          return;
        }

        const cart = await requestJSON(`${route('cart_update_url', '/cart/update')}.js`, body);
        if (notices.length > 0) this.setFlash(notices.join(' '), added.length > 0 ? 'success' : 'info');
        renderSections(cart.sections, targets);
        document.getElementById('main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);
      } finally {
        this.setBusy(false);
      }
    }

    add(slotNumber, button) {
      this.setBusy(true, button);

      return this.enqueue(async () => {
        try {
          const fresh = await fetchFresh();
          const config = fresh.config;
          if (!config) return;

          // Primero se corrige el carrito (y se agregan regalos pendientes) si hace falta.
          const plan = this.resolve(config.plan);
          if (hasWork(plan)) {
            await this.applyPlan(plan, fresh.targets);
            return;
          }

          const slot = config.slots.find((candidate) => candidate.slot === slotNumber);
          if (!slot || slot.state !== 'available') {
            this.setFlash(slot ? blockedReason(slot) : 'Esta promoción ya no está disponible.', 'error');
            renderSections(fresh.sections, fresh.targets);
            return;
          }

          const response = await requestJSON(`${route('cart_add_url', '/cart/add')}.js`, {
            items: [{ id: slot.variantId, quantity: 1, properties: { [SLOT_PROPERTY]: String(slot.slot) } }],
            sections: fresh.targets.map((target) => target.section),
            sections_url: window.location.pathname,
          });
          this.setFlash(`Agregaste ${slot.title} a tu carrito.`, 'success');
          renderSections(response.sections, fresh.targets);
        } catch (error) {
          await this.handleError(error);
        } finally {
          this.setBusy(false, button);
        }
      });
    }

    remove(slotNumber, button) {
      this.setBusy(true, button);

      return this.enqueue(async () => {
        try {
          const fresh = await fetchFresh();
          const slot = fresh.config?.slots.find((candidate) => candidate.slot === slotNumber);
          if (!slot || slot.lineKeys.length === 0) {
            renderSections(fresh.sections, fresh.targets);
            return;
          }

          // El atributo `_promo_auto` no cambia: si era un regalo auto-añadido, queda como rechazado.
          const cart = await requestJSON(`${route('cart_update_url', '/cart/update')}.js`, {
            updates: Object.fromEntries(slot.lineKeys.map((key) => [key, 0])),
            sections: fresh.targets.map((target) => target.section),
            sections_url: window.location.pathname,
          });
          this.setFlash(`Quitaste ${slot.title} de tu carrito.`, 'info');
          renderSections(cart.sections, fresh.targets);
          document.getElementById('main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);
        } catch (error) {
          await this.handleError(error);
        } finally {
          this.setBusy(false, button);
        }
      });
    }

    /**
     * Validación final antes de ir a pagar: aplica solo correcciones (no auto-añade regalos para
     * no frenar el pago). Devuelve false si tuvo que corregir. Si falla la red no bloquea.
     */
    ensureValid({ onInvalid } = {}) {
      return this.enqueue(async () => {
        if (!this.element || this.element.config?.designMode) return true;

        const fresh = await fetchFresh();
        const plan = fresh.config && this.resolve(fresh.config.plan, { includeAdds: false });
        if (!plan || !hasCorrections(plan)) return true;

        if (onInvalid) onInvalid();
        await this.applyPlan(plan, fresh.targets);
        this.element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }).catch((error) => {
        console.error('[Promos carrito] ensureValid', error);
        return true;
      });
    }

    async refreshSections() {
      const targets = getSectionTargets();
      if (targets.length === 0) return;

      const sections = targets.map((target) => target.section).join(',');
      const response = await fetch(`${route('cart_url', '/cart')}?sections=${sections}`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      renderSections(await response.json(), targets);
    }

    async handleError(error) {
      console.error('[Promos carrito]', error);
      this.setFlash(error.userMessage || GENERIC_ERROR, 'error');
      await this.refreshSections().catch(() => {});
    }

    isLooping(plan) {
      const planKey = JSON.stringify([Object.entries(plan.updates).sort(), plan.adds.map((add) => add.slot)]);
      this.repeatedPlans = planKey === this.lastPlanKey ? this.repeatedPlans + 1 : 1;
      this.lastPlanKey = planKey;

      if (this.repeatedPlans > MAX_REPEATED_PLANS) {
        console.warn('[Promos carrito] Corrección automática detenida: el carrito no converge.', plan);
        return true;
      }
      return false;
    }

    resetLoopGuard() {
      this.lastPlanKey = null;
      this.repeatedPlans = 0;
    }

    setBusy(isBusy, button) {
      this.busyCount = Math.max(0, this.busyCount + (isBusy ? 1 : -1));
      this.paintBusy(this.element);

      if (button && button.isConnected) {
        button.classList.toggle('loading', isBusy);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isBusy);
      }
    }

    paintBusy(element) {
      const isBusy = this.busyCount > 0;
      document.documentElement.classList.toggle('mpc-scroll-lock', isBusy);
      if (!element) return;

      element.classList.toggle('mpc--busy', isBusy);
      element.setAttribute('aria-busy', String(isBusy));
      element.querySelector('.popup_loader')?.classList.toggle('is-visible', isBusy);
      element.querySelectorAll('button[data-mpc-action]').forEach((actionButton) => {
        actionButton.disabled = isBusy;
      });
    }

    setFlash(text, tone) {
      this.flash = { text, tone, expiresAt: Date.now() + FLASH_TTL };
      if (this.element) this.paintFlash(this.element);
    }

    paintFlash(element) {
      const status = element.querySelector('[data-mpc-status]');
      if (!status) return;

      const flash = this.flash && this.flash.expiresAt > Date.now() ? this.flash : null;
      status.hidden = !flash;
      status.textContent = flash ? flash.text : '';
      status.className = flash ? `mpc__status mpc__status--${flash.tone}` : 'mpc__status';
    }
  }

  const controller = new PromoController();

  // ---------------------------------------------------------------------------
  // Custom element
  // ---------------------------------------------------------------------------

  class MultiplePromoCart extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.config = readConfig(this);
      if (!this.config) return;

      this.addEventListener('click', this.onClick);
      controller.paintFlash(this);
      if (controller.busyCount > 0) controller.paintBusy(this);
      controller.requestSync();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const button = event.target.closest('button[data-mpc-action]');
      if (!button || button.disabled || !this.config) return;

      event.preventDefault();
      const slot = Number(button.dataset.slot);
      if (button.dataset.mpcAction === 'add') controller.add(slot, button);
      if (button.dataset.mpcAction === 'remove') controller.remove(slot, button);
    }
  }

  customElements.define('multiple-promo-cart', MultiplePromoCart);

  window.MultiplePromo = {
    ensureValid: (options) => controller.ensureValid(options),
    refresh: () => controller.enqueue(() => controller.refreshSections()),
  };

  // Al volver con "atrás" (p. ej. desde checkout) la página sale del bfcache con HTML viejo.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted || !controller.element) return;
    window.MultiplePromo.refresh().catch((error) => console.error('[Promos carrito] refresh', error));
  });
})();
